import { importJWK, type JWTPayload, jwtVerify, type JWTVerifyGetKey } from 'jose'

const tenantDomain = import.meta.env.VITE_TOKEN_TENANT_DOMAIN || ''
const clientId = import.meta.env.VITE_TOKEN_CLIENT_ID || ''
const audiences = import.meta.env.VITE_TOKEN_AUDIENCES?.split(',') || []
const jwksUri = `https://login.microsoftonline.com/${tenantDomain}/discovery/keys?appid=${clientId}`

interface JWKSCacheEntry {
  key: CryptoKey
  timestamp: number
}

const JWKS_CACHE_TTL_MS = 60 * 60 * 1000
const JWKS_MAX_CACHE_SIZE = 10

class JWKSCache {
  private cache = new Map<string, JWKSCacheEntry>()

  get(kid: string): CryptoKey | null {
    const entry = this.cache.get(kid)
    if (!entry) {
      return null
    }

    if (Date.now() - entry.timestamp > JWKS_CACHE_TTL_MS) {
      this.cache.delete(kid)
      return null
    }

    this.cache.delete(kid)
    this.cache.set(kid, entry)
    return entry.key
  }

  set(kid: string, key: CryptoKey): void {
    if (this.cache.size >= JWKS_MAX_CACHE_SIZE) {
      const firstKey = this.cache.keys().next().value
      if (firstKey) {
        this.cache.delete(firstKey)
      }
    }

    this.cache.set(kid, {
      key,
      timestamp: Date.now(),
    })
  }
}

const jwksCache = new JWKSCache()

async function getKey(kid: string): Promise<CryptoKey> {
  const cachedKey = jwksCache.get(kid)
  if (cachedKey) {
    return cachedKey
  }

  const response = await fetch(jwksUri)
  if (!response.ok) {
    throw new Error(`Failed to fetch JWKS keys: ${response.status}`)
  }

  const keys = (await response.json()) as { keys?: Array<Record<string, unknown>> }
  const key = keys.keys?.find((jwksKey) => jwksKey.kid === kid)
  if (!key) {
    throw new Error('Key not found in JWKS')
  }

  const importedKey = (await importJWK(key as any, 'RS256')) as CryptoKey
  jwksCache.set(kid, importedKey)
  return importedKey
}

const getKeyForVerify: JWTVerifyGetKey = async (header) => {
  if (!header.kid) {
    throw new Error('Missing kid in token header')
  }

  return getKey(header.kid)
}

function validateToken(payload: JWTPayload): boolean {
  if (!payload.appid) {
    return false
  }

  return audiences.includes(String(payload.appid))
}

export async function verifyToken(token: string): Promise<boolean> {
  if (!token || !tenantDomain || !clientId || audiences.length === 0) {
    return false
  }

  try {
    const { payload } = await jwtVerify(token, getKeyForVerify)
    return validateToken(payload)
  } catch (error) {
    console.error('Token verification failed:', error)
    return false
  }
}
